import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LayoutGrid, Search, X } from 'lucide-react';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import ColumnSettingsModal from '../../components/common/ColumnSettingsModal';
import ActionMenu from '../../components/common/ActionMenu';
import exportReportExcel from '../reports/export/reportExcelExporter';
import { utilitiesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';

const DEFAULT_SPARE_THRESHOLD = 0.20;

const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

// Section prefixes match the workbook grouping — flat columns for
// FilterableDataGrid compatibility, but grouped-header row (via
// sectionGroups) restores the visual organization from the workbook.
// isDefaultHidden flags the sections hidden on first load; user can
// un-hide any of them via the 6-dot Modify View menu.
const SECTIONS = [
  { prefix: 'act', label: 'Actual IO Counts',   isDefaultHidden: false },
  { prefix: 'den', label: 'Card Density',       isDefaultHidden: true  },
  { prefix: 'cur', label: 'Current Card Count', isDefaultHidden: true  },
  { prefix: 'csp', label: 'Current Spare %',    isDefaultHidden: true  },
  { prefix: 'ra',  label: 'Spare Req Add',      isDefaultHidden: true  },
  { prefix: 'req', label: 'Required Cards',     isDefaultHidden: true  },
  { prefix: 'asp', label: 'Actual Spare %',     isDefaultHidden: false },
];

const IoCountByPanelPage = () => {
  const { notify } = useNotifications();

  const [ioKinds, setIoKinds]                     = useState([]);
  const [rawRows, setRawRows]                     = useState([]);
  const [loading, setLoading]                     = useState(false);
  const [spareThresholdPct, setSpareThresholdPct] = useState(DEFAULT_SPARE_THRESHOLD * 100);
  const [appliedThreshold, setAppliedThreshold]   = useState(DEFAULT_SPARE_THRESHOLD);
  const [searchTerm, setSearchTerm]               = useState('');
  const [filters, setFilters]                     = useState({});

  // Column customization
  const [columnOrder, setColumnOrder]     = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showColSettings, setShowColSettings] = useState(false);
  const [colDefaultsInit, setColDefaultsInit] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await utilitiesApi.getIoCounts(appliedThreshold);
      setIoKinds(res.data.ioKinds || []);
      setRawRows(res.data.data || []);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to load IO counts');
    } finally {
      setLoading(false);
    }
  }, [appliedThreshold, notify]);

  useEffect(() => { loadData(); }, [loadData]);

  const flatRows = useMemo(() => rawRows.map(r => {
    const out = {
      id:           r.panel,
      panel:        r.panel,
      panelType:    r.panelType,
      actTotal:     r.actualTotal,
      slotIo:       r.slotIo,
      slotPlc:      r.slotPlc,
      slotNet:      r.slotNet,
      slotTotal:    r.slotTotal,
      insufficient: !!r.insufficient,
    };
    ioKinds.forEach(k => {
      out[`act_${k}`] = r.actual[k];
      out[`den_${k}`] = r.density[k];
      out[`cur_${k}`] = r.currentCards[k];
      out[`csp_${k}`] = r.currentSparePct[k];
      out[`ra_${k}`]  = r.spareReqAdd[k];
      out[`req_${k}`] = r.requiredCards[k];
      out[`asp_${k}`] = r.actualSparePct[k];
    });
    return out;
  }), [rawRows, ioKinds]);

  const columns = useMemo(() => {
    const cols = [
      { key: 'panel',     label: 'Panel', width: 220 },
      { key: 'panelType', label: 'Type',  width: 60 },
    ];

    // Actual + Actual Total
    ioKinds.forEach(k => cols.push({
      key: `act_${k}`, label: k, settingsLabel: `Actual — ${k}`, width: 56, alignment: 'right',
    }));
    cols.push({ key: 'actTotal', label: 'Total', settingsLabel: 'Actual — Total', width: 72, alignment: 'right' });

    SECTIONS.slice(1).forEach(sec => {
      ioKinds.forEach(k => {
        const isPct = sec.prefix === 'csp' || sec.prefix === 'asp';
        cols.push({
          key: `${sec.prefix}_${k}`,
          label: k,
          settingsLabel: `${sec.label} — ${k}`,
          width: isPct ? 62 : 56,
          alignment: 'right',
          formatter: isPct ? fmtPct : undefined,
        });
      });
    });

    // Slot Counts — label without "Slot" prefix, since the section header covers that
    cols.push({ key: 'slotIo',       label: 'IO',    settingsLabel: 'Slot Counts — IO',        width: 56, alignment: 'right' });
    cols.push({ key: 'slotPlc',      label: 'PLC',   settingsLabel: 'Slot Counts — PLC',       width: 56, alignment: 'right' });
    cols.push({ key: 'slotNet',      label: 'NET',   settingsLabel: 'Slot Counts — NET',       width: 56, alignment: 'right' });
    cols.push({ key: 'slotTotal',    label: 'Total', settingsLabel: 'Slot Counts — Total',     width: 72, alignment: 'right' });
    cols.push({ key: 'insufficient', label: 'Short?', settingsLabel: 'Slot Counts — Short?',   width: 76, alignment: 'center' });

    return cols;
  }, [ioKinds]);

  // Set default hidden columns once ioKinds arrive — user's subsequent
  // toggles via 6-dot menu persist for the session.
  useEffect(() => {
    if (colDefaultsInit || ioKinds.length === 0) return;
    const hidden = [];
    SECTIONS.forEach(sec => {
      if (!sec.isDefaultHidden) return;
      ioKinds.forEach(k => hidden.push(`${sec.prefix}_${k}`));
    });
    setHiddenColumns(hidden);
    setColDefaultsInit(true);
  }, [ioKinds, colDefaultsInit]);

  // Grouped headers — one entry per section covering all its per-kind columns
  // plus 'actTotal' on the Actual section and the Slot section over the fixed
  // slot columns. FilterableDataGrid handles hidden columns automatically —
  // a group with all-hidden keys just collapses to nothing visible.
  const sectionGroups = useMemo(() => {
    const groups = [];
    SECTIONS.forEach(sec => {
      const keys = ioKinds.map(k => `${sec.prefix}_${k}`);
      if (sec.prefix === 'act') keys.push('actTotal');
      groups.push({ label: sec.label, keys });
    });
    groups.push({ label: 'Slot Counts', keys: ['slotIo', 'slotPlc', 'slotNet', 'slotTotal', 'insufficient'] });
    return groups;
  }, [ioKinds]);

  const searchFiltered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return flatRows;
    return flatRows.filter(r => r.panel.toLowerCase().includes(q));
  }, [flatRows, searchTerm]);

  // What Excel export should include: search-filtered rows further pared by
  // any per-column filter dropdowns the user has applied. Mirrors
  // FilterableDataGrid.getFilteredData().
  const exportRows = useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) return searchFiltered;
    return searchFiltered.filter(row =>
      Object.entries(filters).every(([colKey, vals]) => {
        if (!vals || vals.length === 0) return true;
        return vals.includes(String(row[colKey] ?? ''));
      })
    );
  }, [searchFiltered, filters]);

  const applyThreshold = () => {
    const pct = parseFloat(spareThresholdPct);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      notify.warning('Spare threshold must be between 0 and 100%');
      return;
    }
    setAppliedThreshold(pct / 100);
  };

  const handleColSettingsApply = ({ columnOrder: order, hiddenColumns: hidden }) => {
    setColumnOrder(order);
    setHiddenColumns(hidden);
  };

  const handleDownload = async () => {
    if (exportRows.length === 0) {
      notify.warning('No data to export');
      return;
    }
    const hiddenSet = new Set(hiddenColumns);
    // Respect user's column order and hides. Use settingsLabel (context-rich)
    // as the Excel header so downstream readers can tell "Actual — AI" from
    // "Card Density — AI" even though the on-screen labels are just "AI".
    const activeCols = (columnOrder.length > 0
      ? columnOrder.map(k => columns.find(c => c.key === k)).filter(Boolean)
      : columns
    ).filter(c => !hiddenSet.has(c.key));
    const exportCols = activeCols.map(c => ({
      field: c.key,
      label: c.settingsLabel || c.label,
      align: c.alignment,
    }));
    // Excel wants raw fractions rendered as % — pre-format percentage columns.
    const pctKeys = new Set(activeCols.filter(c => c.formatter).map(c => c.key));
    const rowsForExport = exportRows.map(r => {
      const out = { ...r };
      pctKeys.forEach(k => {
        if (typeof out[k] === 'number') out[k] = `${(out[k] * 100).toFixed(1)}%`;
      });
      return out;
    });
    try {
      await exportReportExcel({
        config: { reportName: 'IO Count By Panel' },
        columns: exportCols,
        data: rowsForExport,
        isCancelled: () => false,
        onProgress: () => {},
      });
      notify.success('Download complete');
    } catch (err) {
      notify.error(`Download failed: ${err.message || err}`);
    }
  };

  return (
    <div className="stui-module-container">

      {/* HEADER */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <LayoutGrid size={20} />
          <h1>IO Count By Panel</h1>
          <span className="stui-module-header-meta">
            {loading ? 'Loading…' : `${searchFiltered.length} of ${flatRows.length} panel${flatRows.length !== 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="stui-module-header-actions">

          {/* Spare threshold */}
          <div className="stui-field-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <label style={{ whiteSpace: 'nowrap' }}>Spare Threshold</label>
            <input
              type="number"
              className="stui-field-input"
              style={{ width: 68 }}
              min="0" max="100" step="1"
              value={spareThresholdPct}
              onChange={e => setSpareThresholdPct(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyThreshold(); }}
            />
            <span>%</span>
            <button className="stui-btn stui-btn-secondary" onClick={applyThreshold} disabled={loading}>Apply</button>
          </div>

          {/* Search */}
          <div className="stui-search-wrapper">
            {searchTerm
              ? <X size={16} className="stui-search-icon stui-search-icon-clearable" onClick={() => setSearchTerm('')} />
              : <Search size={16} className="stui-search-icon" />
            }
            <input
              type="text"
              className="stui-search-input"
              placeholder="Search panel..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {/* 6-dot menu: Refresh · Modify View · Download */}
          <ActionMenu
            onRefresh={loadData}
            onModifyView={() => setShowColSettings(true)}
            showModifyView={true}
            onDownload={handleDownload}
          />
        </div>
      </div>

      {/* GRID — wrapped in stui-datasheet-view to inherit the standard padding */}
      <div className="stui-datasheet-view">
        <FilterableDataGrid
          data={searchFiltered}
          allData={flatRows}
          columns={columns}
          filters={filters}
          setFilters={setFilters}
          columnOrder={columnOrder}
          hiddenColumns={hiddenColumns}
          frozenColumns={['panel']}
          sectionGroups={sectionGroups}
        />
      </div>

      <ColumnSettingsModal
        isOpen={showColSettings}
        onClose={() => setShowColSettings(false)}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        frozenColumns={['panel']}
        onApply={handleColSettingsApply}
      />
    </div>
  );
};

export default IoCountByPanelPage;
